import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@mui/styles';
import ListItem from '@mui/material/ListItem';
import ListSubheader from '@mui/material/ListSubheader';
import Button from '@mui/material/Button';
import { NavLink, useLocation } from 'react-router-dom';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';

import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const styles = theme => ({
    root: {
        flexDirection: 'column',
        alignItems: 'start',
        display: 'flex',
        paddingBottom: '0 !important',
        paddingTop: '0 !important',
    },
    list: {
        backgroundColor: theme.palette.background.paper,
    },
    listItem: {
        textAlign: 'left',
        textTransform: 'none',
        display: 'flex',
        paddingTop: '0 !important',
        paddingBottom: '0 !important',
        width: '100%',
        minHeight: 48,
        alignItems: 'center',
        boxSizing: 'border-box',
        paddingLeft: 16,
        paddingRight: 16,
    },
    listItemActive: {
        backgroundColor: '#3085d6 !important',
        color: '#ffffff',
    },
    listItemButtonLabel: {
        flexDirection: 'row',
    },
    listItemText: {
        flex: '1 1 100%',
        textTransform: 'none !important',
        color: '#ffffff', // Set the text color to white
    },
    listIcon: {
        height: 15,
        paddingLeft: 0,
        paddingRight: 16,
        color: '#ffffff', // Set the icon color to white
    },
    nested: {
        width: '100%',
    },
    navLink: {
        textDecoration: 'none',
        color: 'inherit',
        display: 'block',
        width: '100%',
        '&.active': {
            backgroundColor: '#3085d6',
            '& $listItemText': {
                color: '#ffffff',
            },
            '& $listIcon': {
                color: '#ffffff',
            },
        },
    },
    childNavLink: {
        textDecoration: 'none',
        color: 'inherit',
        display: 'block',
        width: '100%',
        paddingLeft: 32, // Indent child items
        '&.active': {
            backgroundColor: '#3085d6',
            '& $listItemText': {
                color: '#ffffff',
            },
            '& $listIcon': {
                color: '#ffffff',
            },
        },
    },
});

const MenuSidenavItem = ({ children, classes, href, title, icon, type, isChild, isChildSubmenu }) => {
    const [open, setOpen] = React.useState(false);
    const location = useLocation();

    const handleClick = () => {
        if (!href) {
            setOpen(prevOpen => !prevOpen);
        }
    };

    const checkIsActive = path => {
        if (isChild) {
            return location.pathname === path;
        }
        return location.pathname.startsWith(path);
    };

    if (type && type === 'header') {
        return (
            <ListSubheader disableSticky className={classes.root}>
                {title}
            </ListSubheader>
        );
    }

    const iconStyle = {
        fontSize: 16,
        color: '#ffffff',
    };

    const content = (
        <Button
            onClick={handleClick}
            classes={{
                root: classes.listItem,
                label: classes.listItemButtonLabel,
            }}
        >
            {icon && <span className={classes.listIcon}>{icon}</span>}
            <Typography variant="button" className={classes.listItemText}>
                {title}
            </Typography>
            {children && (open ? <ExpandLessIcon style={iconStyle} /> : <ExpandMoreIcon style={iconStyle} />)}
        </Button>
    );

    return (
        <ListItem
            className={`${classes.root} ${isChild ? 'child-menu' : ''} ${isChildSubmenu ? 'child-sub-menu' : ''}`}
            disableGutters
        >
            {href ? (
                <NavLink
                    to={href}
                    className={({ isActive }) =>
                        `${isChild ? classes.childNavLink : classes.navLink} ${checkIsActive(href) ? 'active' : ''}`
                    }
                >
                    {content}
                </NavLink>
            ) : (
                content
            )}
            {children && (
                <Collapse
                    in={open}
                    timeout="auto"
                    unmountOnExit
                    className={`${classes.nested} ${open ? 'Active' : ''}`}
                >
                    {children}
                </Collapse>
            )}
        </ListItem>
    );
};

MenuSidenavItem.defaultProps = {
    children: null,
    href: null,
    icon: null,
    type: null,
    isChild: false,
    isChildSubmenu: false,
};

MenuSidenavItem.propTypes = {
    classes: PropTypes.shape({}).isRequired,
    children: PropTypes.node,
    href: PropTypes.string,
    title: PropTypes.string.isRequired,
    icon: PropTypes.node,
    type: PropTypes.string,
    isChild: PropTypes.bool,
    isChildSubmenu: PropTypes.bool,
};

export default withStyles(styles)(MenuSidenavItem);
